import { Component, OnInit, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../material.module';
import { FormsModule } from '@angular/forms';
import { ResponseService } from 'src/services/response.service';
import { combineLatest, debounceTime, EMPTY, iif, map, Observable, switchMap, tap} from 'rxjs';
import { SharedDataService } from 'src/services/shared-data.service';
import { Section } from 'src/models/question.model';
import { RadioButtonVerticalComponent } from './radio-button-vertical/radio-button-vertical.component';
import { DropDownListAnswerComponent } from './drop-down-list-answer/drop-down-list-answer.component';
import { QuestionNoAnswerComponent } from './question-no-answer/question-no-answer.component';
import { AnswerTextboxComponent } from './answer-textbox/answer-textbox.component';
import { SkipLogicQuestionComponent } from './skip-logic-question/skip-logic-question.component';
import { ReplacePatientTermPipe, SanitizeHtmlPipe } from './filter-section.pipe';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { LaunchStatus, ResponseStatus, ResponseTypes } from 'src/models/app-enums.model';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { WatermarkComponent } from '../shared/watermark/watermark.component';
import { QuestionsService } from './questions.service';
import { QuestionnaireResponseAnswer, QuestionnaireResponseQuestion } from 'src/models/questionnaire-response.model';
import { ResponseUpdateRequest, Response } from 'src/models/response.model';
import { AppService } from '../app.service';
import { RadioButtonHorizontalComponent } from './radio-button-horizontal/radio-button-horizontal.component';
import { DateUtil } from '../shared/utils/date.util';
import { LaunchService } from 'src/services/launch.service';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-questions',
  templateUrl: './questions.component.html',
  styleUrls: ['./questions.component.css'],
  standalone: true,
  imports: [MaterialModule,
    FormsModule,
    CommonModule,
    RadioButtonVerticalComponent,
    RadioButtonHorizontalComponent,
    DropDownListAnswerComponent,
    QuestionNoAnswerComponent,
    AnswerTextboxComponent,
    SkipLogicQuestionComponent,
    ReplacePatientTermPipe,
    ClipboardModule,
    SanitizeHtmlPipe,
    WatermarkComponent
  ]
})

export class QuestionsComponent implements OnInit {

  ResponseTypesEnum = ResponseTypes;

  private otherAnswerPlaceholder: string = $localize`:Other Answer Character Count|Maximum number of caracters allowed to be entered in to the other answers box@otherAnswerCharacterMaximumCount:maximum 80 characters`;
  
  get otherAnswerPlaceholderLocalized(): string{
    return this.otherAnswerPlaceholder;
  }

  previousUrl: string | undefined
  currentSection!: Section;
  isPreview: boolean = false;
  patientTerm!: string;
  isPhonePortrait = false;
  isQuestionSaving: boolean = false;
  isLoading: boolean = true;

  constructor(private _responsive: BreakpointObserver,
    private router: Router,
    protected sharedDataService: SharedDataService,
    private responseService: ResponseService,
    public dialog: MatDialog,
    protected questionsService: QuestionsService,
    protected _appService: AppService,
    private _launchService: LaunchService,
  ) {
      this.previousUrl = this.router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString()
  }
  
  dropDownQuestionValidationList = [
    { questionId: '', hasError: false }
  ];

  ngOnInit(): void {

    this.sharedDataService.currentIsQuestionSaving.pipe(debounceTime(500))
      .subscribe((isQuestionSaving: boolean) => {
        this.isQuestionSaving = isQuestionSaving;
      })

    this._responsive.observe([Breakpoints.Small,Breakpoints.HandsetPortrait]).subscribe(result => {
      this.isPhonePortrait = false;
      if (result.matches) {
        this.isPhonePortrait = true;
      }
    });

    combineLatest([
      this.sharedDataService.questionnaireResponse$,
      this.sharedDataService.instrument$,
      this.sharedDataService.launch$,
      this.sharedDataService.previewMode$
    ]).subscribe(([questionnaireResponse, instrument, launch, previewMode]) => {

        // navigate to survey closed page if survey was closed
        const currentTimestamp: number = (new Date()).getTime()
        const endDate: number = (new Date(launch!.endDate)).getTime();
        if (currentTimestamp > endDate || launch!.launchStatus.toLowerCase() === LaunchStatus.LaunchClosed){
          this.router.navigateByUrl('surveyClosed/' + `${launch!.launchId}`)
        }

        if(questionnaireResponse) {
          if (questionnaireResponse.submitStatus?.toUpperCase() == ResponseStatus.responseSubmitted) {
            this.router.navigateByUrl(`${'thankyou/' + questionnaireResponse.responseId}`);
          }
        }

        if(instrument && questionnaireResponse) {

          this.questionsService.instrument = instrument
          this.questionsService.allQuestions = questionnaireResponse.questions
          this.isPreview = previewMode

          // navigate to the section that has a question not answered
          if(questionnaireResponse.questions && this.previousUrl !== '/response/' + questionnaireResponse.responseId) {
            let sectionName = questionnaireResponse.questions[0].surveySection
            for(let question of questionnaireResponse.questions || []) {

              // skip the following question types since the answer to those are always false
              if( question.responseType === ResponseTypes.QuestionWithNoAnswers ||
                  // question.responseType === ResponseTypes.Textbox ||
                  question.responseType === ResponseTypes.TermsAndConditions ) {
                continue
              }

              if(sectionName !== question.surveySection) {
                sectionName = question.surveySection
                this.questionsService.nextSection()
              }

              if(!question.isAnswered){
                break
              }
            }
          }
          this.isLoading = false
          
          // check and assign patient term if selected
          this.dropDownFirstQuestionSaved()
        }
      })

    // update the current section on pev and next buttons
    this.questionsService.section$.subscribe((section) => {
      if(section){
        this.currentSection = section
      }
    })

    //GOTO top of page
    let top = document.getElementById('top');
    if (top !== null) {
      top.scrollIntoView();
      top = null;
    }
  }

  /**
   * 
   * @param validation 
   */
  dropDownComponentValidation(validation: any) {
    if (this.dropDownQuestionValidationList.filter(e => e.questionId === validation[1].questionId).length < 1) {
      this.dropDownQuestionValidationList.push(validation[1]);
    } else {
      let index = this.dropDownQuestionValidationList.findIndex(e => e.questionId === validation[1].questionId);
      this.dropDownQuestionValidationList[index].hasError = validation[1].hasError;
    }
  }

  /**
   * 
   * @returns 
   */
  checkSectionQuestionCompleteness(): boolean {
    
    // check dropdown validation error
    const dropDownHasError: boolean = this.dropDownQuestionValidationList.some(e => e.hasError)

    // pre conditions
    if(dropDownHasError || this.isLoading){
      return false;
    }

    // update progress bar
    this.sharedDataService.SetQuestionnaireProgress(this.questionnaireProgressCount());
    // get countable questions in current section
    let questionsInSection: QuestionnaireResponseQuestion[] = this.questionsService.questionsInCurrentSection?.
      filter((question) => 
        question.surveySection === this.currentSection.sectionName?.toUpperCase() &&
        question.responseType !== ResponseTypes.QuestionWithNoAnswers && 
        question.responseType !== ResponseTypes.Textbox && 
        question.responseType !== ResponseTypes.TermsAndConditions
      );
    
    return !questionsInSection?.some(q => !q.isAnswered) && !this.isQuestionSaving
  }

  /**
   * 
   */
  dropDownFirstQuestionSaved() {
    // set pre condition 
    if(this.isLoading) { return }

    let dropDownListQuestions: QuestionnaireResponseQuestion[] = this.questionsService.allQuestions.filter((question) => question.responseType === ResponseTypes.DropDownList);
    for (let dropDownListQuestion of dropDownListQuestions ?? []) {
      let answers: QuestionnaireResponseAnswer[] = dropDownListQuestion.answers?.filter((answer) => answer.selected)
      if (answers.length > 0 && answers[0].patientTerm) {
        this.sharedDataService.patientTerm = answers[0].patientTerm
      }
    }
  }

  /**
   * 
   * @returns 
   */
  questionnaireProgressCount(): number {

    // set pre condition 
    if(this.isLoading) { return 0 }

    const countableQuestions: QuestionnaireResponseQuestion[] = this.questionsService.allQuestions.filter(q => q.responseType !== ResponseTypes.QuestionWithNoAnswers && q.responseType !== ResponseTypes.Textbox && q.responseType !== ResponseTypes.TermsAndConditions)
    // questions answered
    let answeredQuestionsCount: number = 0

    countableQuestions.forEach(q => {
      if(q.isAnswered) {
        answeredQuestionsCount++;
      }
    })
    return answeredQuestionsCount / countableQuestions.length;
  }

  /**
   * 
   * @returns 
   */
  hideNextButtonEnableSubmit(): boolean {
    // set pre condition 
    if(this.isLoading) { return true }

    const additionalQuestions: QuestionnaireResponseQuestion[]  = this.questionsService.allQuestions.filter(q => q.surveySection === 'Z')
    const additionalQuestionsSection: boolean = (this.questionsService.sections.filter(q => q.sectionName === 'Z')).length > 0

    if (!additionalQuestionsSection)
    {
        return this.questionsService.lastSection === this.currentSection
    }
    
    if ((additionalQuestions === undefined || additionalQuestions?.length === 0) && additionalQuestionsSection)
    {
        return this.questionsService.sections[this.questionsService.sections.length - 2] == this.currentSection;
    }

    return this.questionsService.sections[this.questionsService.sections.length - 1] == this.currentSection;
  }

  /**
   * 
   */
  GoPreviousPage() {
    this.sharedDataService.questionnaireResponse$.subscribe((questionnaireResponse) => {
      this.questionsService.previousSection() ? 
        this._scrollToTop() : 
        this.router.navigateByUrl(`${'response/' + questionnaireResponse!.responseId + (this.isPreview ? "/preview" : "")}`);
    })
  }

  /**
   * 
   */
  GoNextPage(nextButton: MatButton) {
    // locks next button temporarily to solve the racing condition 
    // between the actual next button validity 
    // and the time it takes to render next section and checks the next button validity
    nextButton.disabled = true
    this._checkLaunchValidity().subscribe((validLaunch) => {
      if(validLaunch) {
        this.questionsService.nextSection()
        // release the lock on the next button
        nextButton.disabled = !this.checkSectionQuestionCompleteness() && !this.isPreview
        this._scrollToTop()
      }
    })
  }

  /**
   * 
   */
  GoFinalPage() {
    // check if launch is valid enddate check/launch status
    this._checkLaunchValidity().pipe(
      // if launch is not valid end/complete the pipe by using the EMPTY observable
      switchMap((validLaunch) => iif(() => validLaunch, this.sharedDataService.questionnaireResponse$, EMPTY)),
      tap((questionnaireResponse) => {
        let responseUpdateRequest: ResponseUpdateRequest = this._appService.convertQuestionnaireResponseToResponseUpdateRequest(questionnaireResponse!, this.isPreview)
        responseUpdateRequest = {
          ...responseUpdateRequest,
          submitStatus: ResponseStatus.responseSubmitted,
          submitTimeStamp: DateUtil.toISOString(new Date())
        }
        // remove questions
        delete responseUpdateRequest.questions
        // call update api
        this.responseService.updateResponse(responseUpdateRequest).subscribe((response: Response) => {
          this.router.navigateByUrl(`${'thankyou/' + response.responseId}`);
          // window.location.reload()
        })
      })
    ).subscribe()
  }

  /**
   * 
   */
  saveContinueLater() {
    this._checkLaunchValidity().subscribe((validLaunch) => {
      if(validLaunch) {
        this.dialog.open(SaveContinueLaterDialog, {
          data: {
            text: window.location.href
          },
          width: '735px',
          height: '380px',
        });
      }
    })    
  }

  /**
   * 
   * @returns 
   */
  private _checkLaunchValidity(): Observable<boolean> {
    const launchId = this.sharedDataService.launch?.launchId
    return this._launchService.getLaunch(launchId).pipe(
      map(launch => {
        // navigate to survey closed page if survey was closed
        const currentTimestamp: number = (new Date()).getTime()
        const endDate: number = (new Date(launch.endDate)).getTime();
        if (currentTimestamp > endDate || launch.launchStatus.toLowerCase() === LaunchStatus.LaunchClosed){
          this.router.navigateByUrl('surveyClosed/' + `${launch.launchId}`)
          return false
        }
        return true
      })
    )
  }

  /**
   * 
   */
  private _scrollToTop(){
    setTimeout(() => { window.scrollTo(0,0) }, 1000)
  }
}

@Component({
  selector: 'save-continue-later-dialog',
  templateUrl: 'save-continue-later-dialog.html',
  styleUrls: ['./questions.component.css'],
  standalone: true,
  imports: [MaterialModule, ClipboardModule],
})
export class SaveContinueLaterDialog {
  constructor(@Inject(MAT_DIALOG_DATA) public data: {text: string | undefined}) { }

  public getSaveContinueLaterUrl() {
    return window.location.href;
  }
}
